import { Check, LockKeyhole, Minus } from 'lucide-react';
import { CardList } from '@/components/ui/CardList';
import { cn } from '@/utils/cn';
import { buildPermissionKey } from '../../catalog/permissionCatalog';
import type { PermissionCatalogAction } from '../../catalog/permissionCatalog';
import type { PermissionModule, PermissionRole } from '../types/permissionsList.types';

interface PermissionsMatrixProps {
  modules: PermissionModule[];
  roles: PermissionRole[];
  isLoading: boolean;
  savingKey: string | null;
  onToggle: (role: PermissionRole, permissionKey: string, enabled: boolean) => void;
}

interface MatrixRow {
  module: PermissionModule;
  resource: PermissionModule['resources'][number];
  action: PermissionCatalogAction;
  key: string;
}

interface MatrixViewProps {
  rows: MatrixRow[];
  roles: PermissionRole[];
  isLoading: boolean;
  savingKey: string | null;
  onToggle: (role: PermissionRole, permissionKey: string, enabled: boolean) => void;
}

const permissionColumnWidth = 260;
const riskColumnWidth = 88;
const roleColumnWidth = 88;

export function PermissionsMatrix({ modules, roles, isLoading, savingKey, onToggle }: PermissionsMatrixProps) {
  const rows = modules.flatMap((module) =>
    module.resources.flatMap((resource) =>
      resource.actions.map((action) => ({
        module,
        resource,
        action,
        key: buildPermissionKey(module.code, resource.code, action.code),
      })),
    ),
  );

  return (
    <div className="min-w-0 max-w-full space-y-2 overflow-x-hidden">
      <div className="min-w-0 max-w-full overflow-hidden rounded-md border border-slate-100 bg-white p-3 shadow-sm">
        <div className="flex min-w-0 flex-col gap-3 text-sm md:flex-row md:items-center md:justify-between">
          <div className="flex min-w-0 items-center gap-2">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-700">
              <LockKeyhole className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <h2 className="truncate font-medium text-slate-900">Ma trận quyền</h2>
              <p className="line-clamp-2 text-slate-500">Bật hoặc tắt quyền trực tiếp cho từng vai trò.</p>
            </div>
          </div>
          <div className="grid min-w-0 grid-cols-2 gap-2 sm:flex sm:items-center">
            <LegendItem label="Đang bật" className="border-emerald-500 bg-emerald-500 text-white" />
            <LegendItem label="Đang tắt" className="border-slate-200 bg-white text-slate-300" />
          </div>
        </div>
      </div>

      <DesktopMatrix rows={rows} roles={roles} isLoading={isLoading} savingKey={savingKey} onToggle={onToggle} />
      <MobileMatrix rows={rows} roles={roles} isLoading={isLoading} savingKey={savingKey} onToggle={onToggle} />
    </div>
  );
}

function DesktopMatrix({ rows, roles, isLoading, savingKey, onToggle }: MatrixViewProps) {
  const tableWidth = permissionColumnWidth + riskColumnWidth + Math.max(roles.length, 1) * roleColumnWidth;

  return (
    <div className="hidden w-full min-w-0 max-w-full overflow-hidden rounded-md border border-slate-100 bg-white shadow-sm lg:block">
      <div className="max-h-[680px] w-full min-w-0 max-w-full overflow-auto">
        <table
          className="table-fixed border-separate border-spacing-0 text-left text-sm"
          style={{ width: tableWidth, minWidth: tableWidth }}
        >
          <thead className="sticky top-0 z-20 bg-white shadow-sm font-normal">
            <tr className="text-slate-500">
              <th
                className="sticky left-0 z-30 border-b border-r border-slate-100 bg-white p-2 text-center font-normal"
                style={{ width: permissionColumnWidth, minWidth: permissionColumnWidth, maxWidth: permissionColumnWidth }}
              >
                <span className="block truncate">Quyền</span>
              </th>
              <th
                className="sticky z-30 border-b border-r border-slate-100 bg-white p-2 text-center font-normal"
                style={{ left: permissionColumnWidth, width: riskColumnWidth, minWidth: riskColumnWidth, maxWidth: riskColumnWidth }}
              >
                <span className="block truncate">Rủi ro</span>
              </th>
              {roles.map((role) => (
                <th
                  key={role.id}
                  className="border-b border-slate-100 bg-white px-1.5 py-2 text-center font-normal"
                  style={{ width: roleColumnWidth, minWidth: roleColumnWidth, maxWidth: roleColumnWidth }}
                >
                  <span className="mx-auto block max-w-full whitespace-normal break-words leading-snug line-clamp-2">{role.name}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 8 }).map((_, index) => (
                <tr key={index} className="animate-pulse">
                  <td colSpan={roles.length + 2} className="p-3">
                    <div className="h-10 rounded-md bg-slate-50" />
                  </td>
                </tr>
              ))
            ) : rows.length ? (
              rows.map((row) => (
                <tr key={row.key} className="group">
                  <td
                    className="sticky left-0 z-10 overflow-hidden border-b border-r border-slate-50 bg-white p-2 group-hover:bg-slate-50"
                    style={{ width: permissionColumnWidth, minWidth: permissionColumnWidth, maxWidth: permissionColumnWidth }}
                  >
                    <PermissionInfo row={row} />
                  </td>
                  <td
                    className="sticky z-10 overflow-hidden border-b border-r border-slate-50 bg-white p-2 group-hover:bg-slate-50"
                    style={{ left: permissionColumnWidth, width: riskColumnWidth, minWidth: riskColumnWidth, maxWidth: riskColumnWidth }}
                  >
                    <RiskBadge risk={row.action.risk} />
                  </td>
                  {roles.map((role) => (
                    <PermissionCell key={role.id} role={role} permissionKey={row.key} savingKey={savingKey} onToggle={onToggle} />
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={roles.length + 2} className="p-8 text-center text-sm text-slate-400">Không có quyền phù hợp</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MobileMatrix({ rows, roles, isLoading, savingKey, onToggle }: MatrixViewProps) {
  return (
    <div className="min-w-0 max-w-full overflow-hidden lg:hidden">
      <CardList
        items={rows}
        isLoading={isLoading}
        emptyMessage="Không có quyền phù hợp"
        renderCard={(row) => (
          <article key={row.key} className="min-w-0 overflow-hidden rounded-md border border-slate-100 bg-white p-3 shadow-sm">
            <div className="min-w-0 space-y-2 text-sm">
              <div className="flex min-w-0 items-start justify-between gap-2">
                <PermissionInfo row={row} />
                <RiskBadge risk={row.action.risk} />
              </div>
              <div className="grid min-w-0 grid-cols-1 gap-2 border-t border-slate-100 pt-2 sm:grid-cols-2">
                {roles.map((role) => {
                  const isAdmin = role.code === 'super_admin' || role.code === 'admin';
                  const checked = isAdmin || role.permissionKeys.includes(row.key);
                  const loading = savingKey === `${role.id}:${row.key}`;
                  return (
                    <button
                      key={role.id}
                      type="button"
                      disabled={loading || isAdmin}
                      onClick={() => onToggle(role, row.key, !checked)}
                      className={cn(
                        'flex min-h-10 min-w-0 items-center justify-between gap-2 overflow-hidden rounded-md border p-3 text-sm disabled:opacity-50',
                        checked ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-slate-100 bg-white text-slate-600',
                      )}
                    >
                      <span className="min-w-0 flex-1 truncate text-left font-semibold">{role.name}</span>
                      <span className="shrink-0">
                        <ToggleIcon checked={checked} loading={loading} />
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </article>
        )}
      />
    </div>
  );
}

function PermissionCell({ role, permissionKey, savingKey, onToggle }: {
  role: PermissionRole;
  permissionKey: string;
  savingKey: string | null;
  onToggle: (role: PermissionRole, permissionKey: string, enabled: boolean) => void;
}) {
  const isAdmin = role.code === 'super_admin' || role.code === 'admin';
  const checked = isAdmin || role.permissionKeys.includes(permissionKey);
  const loading = savingKey === `${role.id}:${permissionKey}`;

  return (
    <td
      className="overflow-hidden border-b border-slate-50 px-1.5 py-2 text-center group-hover:bg-slate-50"
      style={{ width: roleColumnWidth, minWidth: roleColumnWidth, maxWidth: roleColumnWidth }}
    >
      <button
        type="button"
        disabled={loading || isAdmin}
        onClick={() => onToggle(role, permissionKey, !checked)}
        className={cn(
          'mx-auto flex h-6 w-6 items-center justify-center rounded-md border text-sm transition-colors disabled:opacity-50',
          checked ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-slate-200 bg-white text-slate-300',
          isAdmin && 'cursor-not-allowed opacity-100',
        )}
        aria-label={`${checked ? 'Tắt' : 'Bật'} ${permissionKey} cho ${role.name}`}
      >
        <ToggleIcon checked={checked} loading={loading} />
      </button>
    </td>
  );
}

function PermissionInfo({ row }: { row: MatrixRow }) {
  return (
    <div className="min-w-0 max-w-full space-y-1 overflow-hidden text-sm">
      <div className="flex min-w-0 flex-wrap items-center gap-1 overflow-hidden">
        <PermissionBadge label={row.module.name} tone="module" />
        <PermissionBadge label={row.resource.name} tone="resource" />
      </div>
      <p className="truncate font-medium text-slate-900">{row.action.name}</p>
      <p className="line-clamp-2 break-words text-slate-500">{row.action.description}</p>
      <p className="truncate text-slate-400">Phạm vi: {row.action.scopes.join(', ')}</p>
    </div>
  );
}

function ToggleIcon({ checked, loading }: { checked: boolean; loading: boolean }) {
  if (loading) return <i className="ri-loader-4-line animate-spin" />;
  if (checked) return <Check className="h-4 w-4" />;
  return <Minus className="h-4 w-4" />;
}

function LegendItem({ label, className }: { label: string; className: string }) {
  return (
    <div className="flex min-w-0 items-center gap-2 rounded-md border border-slate-100 p-3 text-sm">
      <span className={cn('flex h-6 w-6 shrink-0 items-center justify-center rounded-md border', className)}>
        <Check className="h-4 w-4" />
      </span>
      <span className="min-w-0 truncate font-semibold text-slate-600">{label}</span>
    </div>
  );
}

function PermissionBadge({ label, tone }: { label: string; tone: 'module' | 'resource' }) {
  return (
    <span
      className={cn(
        'inline-flex max-w-full items-center gap-1 rounded-md border px-2 py-1 text-sm font-semibold shadow-sm',
        tone === 'module' ? 'border-emerald-100 bg-emerald-50 text-emerald-700' : 'border-blue-100 bg-blue-50 text-blue-700',
      )}
    >
      <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', tone === 'module' ? 'bg-emerald-500' : 'bg-blue-500')} />
      <span className="min-w-0 truncate">{label}</span>
    </span>
  );
}

function RiskBadge({ risk }: { risk: string }) {
  const tone = risk === 'Rất cao'
    ? 'border-red-100 bg-red-50 text-red-700 shadow-red-100'
    : risk === 'Cao'
      ? 'border-rose-100 bg-rose-50 text-rose-700 shadow-rose-100'
      : risk === 'Vừa'
        ? 'border-amber-100 bg-amber-50 text-amber-700 shadow-amber-100'
        : 'border-slate-200 bg-slate-50 text-slate-700 shadow-slate-100';

  return (
    <span className={cn('inline-flex max-w-full shrink-0 rounded-md border px-2 py-1 text-center text-sm font-medium shadow-sm', tone)}>
      <span className="block max-w-full truncate">{risk}</span>
    </span>
  );
}
